/**
 * AccountStatusSettings — Portal Settings → Legal Diary → Account status tags.
 * Emma #14: admin-editable AirTable-style status list (BP Filed, Restructured, …)
 * shared by UK + AUS collections/legal files.
 */
import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Tag, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { getAccountStatuses, saveAccountStatuses } from "@/lib/api";
import { TAG_TONES, invalidateAccountStatuses } from "@/components/collections/StatusTags";

const COLORS = Object.keys(TAG_TONES);

export default function AccountStatusSettings() {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAccountStatuses()
      .then(r => { setRows(r.statuses || []); setMeta({ updated_at: r.updated_at, updated_by: r.updated_by }); })
      .catch(e => toast.error("Failed to load status tags: " + (e?.response?.data?.detail || e.message)));
  }, []);

  const patch = (i, p) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const save = async () => {
    setSaving(true);
    try {
      const r = await saveAccountStatuses(rows.map(x => ({ key: x.key, label: x.label, color: x.color })));
      setRows(r.statuses); setMeta({ updated_at: r.updated_at, updated_by: r.updated_by });
      invalidateAccountStatuses();
      toast.success("Account status tags saved");
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (!rows) return <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> loading status tags…</div>;

  return (
    <Card data-testid="account-status-settings">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Tag className="w-4 h-4 text-fuchsia-600" /> Account status tags</CardTitle>
        <CardDescription>
          Manual statuses handlers can tag on any UK or AUS file (like the old AirTable "BP Filed" / "Restructured" columns). Filterable on the Legal Case Tracker and Loan Arrears Ageing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.map((r, i) => (
          <div key={r.key || i} className="flex items-center gap-2" data-testid={`account-status-row-${i}`}>
            <Input value={r.label} onChange={(e) => patch(i, { label: e.target.value })} placeholder="Status label" className="h-8 text-xs flex-1" data-testid={`account-status-label-${i}`} />
            <select value={r.color || "slate"} onChange={(e) => patch(i, { color: e.target.value })} className="h-8 text-xs border rounded-md px-1 bg-white" data-testid={`account-status-color-${i}`}>
              {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${TAG_TONES[r.color] || TAG_TONES.slate}`}>{r.label || "preview"}</span>
            <button type="button" onClick={() => setRows(rs => rs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`account-status-remove-${i}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <div className="flex items-center justify-between pt-1">
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={rows.length >= 30}
            onClick={() => setRows(rs => [...rs, { key: "", label: "", color: "slate" }])} data-testid="account-status-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add status
          </Button>
          <div className="flex items-center gap-3">
            {meta.updated_at && <span className="text-[10px] text-muted-foreground">Saved {new Date(meta.updated_at).toLocaleString("en-GB")} by {meta.updated_by || "—"}</span>}
            <Button size="sm" className="h-8 text-xs" onClick={save} disabled={saving} data-testid="account-status-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save tags
            </Button>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground">Removing a status here hides it from the pick list; files already tagged keep the key until re-saved.</p>
      </CardContent>
    </Card>
  );
}
